import { forwardRef, type ReactNode } from 'react';
import {
  BookOpen,
  Database,
  FileText,
  Globe,
  List,
  MessageSquare,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { stripMarkdownEscapes } from '@/lib/utils/ai-text';

/**
 * T-0093 / E10 — CitationChip primitive (ADR-002).
 *
 * Citations are the IV&V trust contract: every AI claim must trace to a
 * source passage. The chip + `<CitationSourceModal>` together implement
 * the round-trip — click chip → modal shows the source doc with the cited
 * span highlighted.
 *
 * Visual contract: see
 *   - `design_handoff_caliber_v2/README.md` §6 (Citation chip subsection)
 *   - `design_handoff_caliber_v2/prototype/ui.js` → `citeChip()`
 *   - `design_handoff_caliber_v2/prototype/tokens.css` lines 165-179
 *     (`.cite`, `.cite .loc`, `.cite .conf`, `.cite.broken`)
 *
 * Tokens (via Tailwind theme from T-0090):
 *   bg-cite-chip-bg / text-cite-chip-ink / border-cite-chip-border, plus
 *   text-cite-broken + line-through for broken citations.
 *
 * Reuse rule (CLAUDE.md hard rule #3): this is a load-bearing primitive.
 * Build once, reuse everywhere — do not inline these styles in screens.
 *
 * The chip never grows wider than its container: a long document title
 * truncates (the full label stays in the tooltip), so a chip can't push an
 * AI answer past the right edge of a narrow window (C2-35). Labels and
 * locators come from parsed documents and lose their Markdown escapes
 * ("SOW\-QA" reads "SOW-QA", C2-33).
 */

export type CitationStatus = 'ok' | 'broken';

export interface CitationChipData {
  /** Stable identifier — what the modal fetcher keys off. */
  id: string;
  /** Short document label, e.g. "SOW v2", "Status Report — 05-07". */
  doc_label: string;
  /** Section breadcrumb when present, e.g. "4.3.2". */
  section?: string | null;
  /** 1-indexed page number for page-anchored citations. */
  page?: number | null;
  /** A1-style cell address for spreadsheet citations. */
  cell?: string | null;
  /**
   * T-0155: a spreadsheet citation by sheet and worksheet row, e.g. sheet
   * "Requirements", row 14. Shown as "Requirements · row 14". A row without
   * a sheet is shown as "row 14".
   */
  sheet?: string | null;
  row?: number | null;
  /**
   * T-0155: the source file records no pages (a Word file with no page
   * layout). The chip then never shows a page; it shows the section when
   * there is one, and says "no pages" in its accessible name.
   */
  noPages?: boolean;
  /** AI confidence in the citation (0..1). Rendered to 2 decimals. */
  confidence: number;
  /** When 'broken', the source doc was deleted/not accessible. */
  status?: CitationStatus;
}

export interface CitationChipProps {
  cite: CitationChipData;
  /**
   * Click handler — receives the citation id. When omitted, the chip
   * still renders as a button but does nothing (callers should wire a
   * handler that opens `<CitationSourceModal>`).
   */
  onOpen?: (citationId: string) => void;
  /**
   * False hides the confidence segment, for citations that carry no AI
   * confidence (a report's evidence: confirmed knowledge items and
   * discovery evidence, T-0271). Default true.
   */
  showConfidence?: boolean;
  className?: string;
}

/**
 * Build the human-readable location suffix shown in mono after the
 * doc label. Prototype examples:
 *   "§4.3.2 p.18", "p.4 §3", "p.1", "B12" (cell).
 * T-0155 adds "Requirements · row 14" (sheet and row) and section-only
 * locators for files that record no pages.
 *
 * Empty string when nothing locates the cite — the chip then renders
 * with just the label + confidence (still valid per the prototype).
 */
function formatLocator(cite: CitationChipData): string {
  const parts: string[] = [];
  if (cite.section) parts.push(`§${stripMarkdownEscapes(cite.section)}`);
  if (!cite.noPages && typeof cite.page === 'number' && Number.isFinite(cite.page)) {
    parts.push(`p.${cite.page}`);
  }
  if (cite.cell) parts.push(cite.cell);
  const row = typeof cite.row === 'number' && Number.isFinite(cite.row) ? `row ${cite.row}` : null;
  if (cite.sheet) parts.push(row ? `${cite.sheet} · ${row}` : cite.sheet);
  else if (row) parts.push(row);
  return parts.join(' ');
}

/**
 * Compose the aria-label per ticket spec:
 *   "View citation: {doc_label} §{section}, page {page}"
 * Falls back gracefully when section / page are absent.
 */
function buildAriaLabel(cite: CitationChipData): string {
  const bits: string[] = [`View citation: ${stripMarkdownEscapes(cite.doc_label)}`];
  if (cite.section) bits.push(`§${stripMarkdownEscapes(cite.section)}`);
  if (cite.noPages) bits.push('the file records no pages');
  else if (typeof cite.page === 'number' && Number.isFinite(cite.page))
    bits.push(`page ${cite.page}`);
  if (cite.cell) bits.push(`cell ${cite.cell}`);
  if (cite.sheet) bits.push(`sheet ${cite.sheet}`);
  if (typeof cite.row === 'number' && Number.isFinite(cite.row)) bits.push(`row ${cite.row}`);
  if (cite.status === 'broken') bits.push('(broken citation)');
  return bits.join(', ');
}

export const CitationChip = forwardRef<HTMLButtonElement, CitationChipProps>(function CitationChip(
  { cite, onOpen, showConfidence = true, className },
  ref,
) {
  const broken = cite.status === 'broken';
  const label = stripMarkdownEscapes(cite.doc_label);
  const loc = formatLocator(cite);
  const confidenceText = cite.confidence.toFixed(2);
  const handleClick = () => {
    if (onOpen) onOpen(cite.id);
  };

  return (
    <button
      ref={ref}
      type="button"
      role="button"
      aria-label={buildAriaLabel(cite)}
      // Matches the prototype's citeChip(): the full label and location on hover.
      title={loc ? `${label} · ${loc}` : label}
      data-cite-id={cite.id}
      data-cite-status={broken ? 'broken' : 'ok'}
      onClick={handleClick}
      className={cn(
        // Pill: soft blue-grey background, navy ink, hairline border.
        'inline-flex h-6 max-w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border pl-2.5 pr-2 text-[12px] font-medium transition-colors',
        'bg-cite-chip-bg text-cite-chip-ink border-cite-chip-border',
        'hover:border-ink-300',
        // Focus ring — keyboard nav has to be visible.
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300 focus-visible:ring-offset-1',
        // Broken: red ink + border + strike-through (matches `.cite.broken`).
        broken && 'text-cite-broken border-cite-broken line-through decoration-1',
        className,
      )}
    >
      <span className="min-w-0 truncate">{label}</span>
      {loc ? (
        <span className="min-w-0 max-w-[240px] truncate font-mono text-[11px] font-normal text-ink-500">
          {loc}
        </span>
      ) : null}
      {showConfidence ? (
        <span
          className={cn(
            // Confidence segment after a vertical divider; never squeezed.
            'shrink-0 border-l border-cite-chip-border pl-2 font-mono text-[11px] font-normal',
            // Color follows confidence band, mirroring the prototype which
            // shows the confidence in the high-band color by default. The
            // strike-through on broken chips suppresses this anyway.
            confidenceColor(cite.confidence),
          )}
        >
          {confidenceText}
        </span>
      ) : null}
    </button>
  );
});

function confidenceColor(c: number): string {
  if (c >= 0.85) return 'text-ai-confidence-high';
  if (c >= 0.65) return 'text-ai-confidence-medium';
  return 'text-ai-confidence-low';
}

// ===========================================================================
// T-0206 — vendor knowledge base citations (E13, ADR-018 §5)
//
// Every knowledge-base citation is rendered by the server's `renderCitation`
// for the caller's audience and arrives as `{ text, parts }`. These forms
// only lay those parts out; they never compose a citation's wording.
// Prototype: `docs/vendor-knowledge/prototype/knowledge.js` → `srcChip()`,
// `citeBlock()` and the `.kb-src`, `.kb-cite`, `.redact` rules of
// `knowledge.css`.
// ===========================================================================

/** The marker the server writes where a protected client name was removed. */
export const CLIENT_NAME_REMOVED = '[client name removed]';
export const AVERO_KB_LABEL = 'Avero Advisors Vendor Knowledge Base';

export type VendorSourceKind = 'web_page' | 'vendor_document' | 'avero_kb';

export interface VendorCitationParts {
  number?: string;
  excerpt?: string;
  answer_label?: string;
  source_label: string;
  url?: string;
  date: string;
  locator_label?: string;
  confidential: boolean;
}

export interface VendorCitationData {
  text: string;
  parts: VendorCitationParts;
}

/** The source kind a set of parts describes (a URL means a web page). */
export function vendorSourceKind(parts: VendorCitationParts): VendorSourceKind {
  if (parts.url) return 'web_page';
  if (parts.source_label === AVERO_KB_LABEL) return 'avero_kb';
  return 'vendor_document';
}

/** `host/…/last-segment`, as the prototype shortens a page address. */
export function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const segs = u.pathname.split('/').filter(Boolean);
    if (segs.length === 0) return host;
    return segs.length === 1 ? `${host}/${segs[0]}` : `${host}/…/${segs[segs.length - 1]}`;
  } catch {
    return url;
  }
}

/**
 * Text with each removed client name shown as a marker chip. `highlight`
 * marks the markers (the "Client name removed" review). Nothing here edits
 * text: quotes are the source's own words.
 */
export function RedactedText({ text, highlight = false }: { text: string; highlight?: boolean }) {
  const parts = text.split(CLIENT_NAME_REMOVED);
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 ? (
            <span
              className={cn(
                'inline-block whitespace-nowrap rounded px-[5px] align-[1px] font-sans text-meta font-medium not-italic leading-5 no-underline',
                highlight
                  ? 'bg-cite-highlight text-ink-900 shadow-[inset_0_0_0_1px_var(--accent-amber-500)]'
                  : 'bg-ink-100 text-ink-700',
              )}
            >
              {CLIENT_NAME_REMOVED}
            </span>
          ) : null}
        </span>
      ))}
    </>
  );
}

const SOURCE_ICON: Record<VendorSourceKind, typeof Globe> = {
  web_page: Globe,
  vendor_document: FileText,
  avero_kb: BookOpen,
};

const KIND_LABEL: Record<VendorSourceKind, string> = {
  web_page: 'Web page',
  vendor_document: 'Vendor document',
  avero_kb: AVERO_KB_LABEL,
};

export interface VendorSourceChipProps {
  /** The citation's parts; without them the chip names the source kind only. */
  parts?: VendorCitationParts;
  kind?: VendorSourceKind | string;
  /** Further citations of the same item (`+2`). */
  more?: number;
  /** Show the citation's date after the label. */
  withDate?: boolean;
  className?: string;
}

/** The compact source pill (`.kb-src`): icon, where the words come from, `+N` further citations. */
export function VendorSourceChip({
  parts,
  kind,
  more = 0,
  withDate = false,
  className,
}: VendorSourceChipProps) {
  const k: VendorSourceKind = parts
    ? vendorSourceKind(parts)
    : kind === 'web_page' || kind === 'vendor_document' || kind === 'avero_kb'
      ? kind
      : 'vendor_document';
  const Icon = SOURCE_ICON[k];
  let label = KIND_LABEL[k];
  let title = label;
  if (parts) {
    if (k === 'web_page' && parts.url) {
      label = shortUrl(parts.url);
      title = parts.url;
    } else if (k === 'vendor_document') {
      label = parts.locator_label
        ? `${parts.source_label}, ${parts.locator_label}`
        : parts.source_label;
      title = label;
    }
  }
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-[22px] max-w-[340px] items-center gap-[5px] rounded-full border border-cite-chip-border bg-cite-chip-bg px-2 align-middle text-[11px] font-medium leading-4 text-cite-chip-ink',
        className,
      )}
    >
      <Icon className="h-3 w-3 flex-none text-ink-500" aria-hidden="true" />
      <span className="min-w-0 truncate">
        {label}
        {withDate && parts ? ` · ${parts.date}` : ''}
      </span>
      {more > 0 ? (
        <span
          className="flex-none border-l border-cite-chip-border pl-1.5 font-mono text-[11px] font-normal text-ink-500"
          aria-label={`and ${more} more`}
        >
          +{more}
        </span>
      ) : null}
    </span>
  );
}

/** Normalised as the server normalises an excerpt inside a citation's text. */
function normaliseForCitation(s: string): string {
  return s.normalize('NFC').replace(/\s+/gu, ' ').trim();
}

/** The reference after the quote (`— title, URL, captured 4 Oct 2026, under “X”.`), from the server's text. */
export function citationReference(c: VendorCitationData): string {
  const { text, parts } = c;
  if (parts.excerpt === undefined) return text;
  let lead = `“${normaliseForCitation(parts.excerpt)}”`;
  if (parts.answer_label) lead += ` (${parts.answer_label})`;
  if (!text.startsWith(lead)) return text;
  return text.slice(lead.length).replace(/^\s*—\s*/u, '');
}

export interface VendorCitationProps {
  citation: VendorCitationData;
  /** Mark removed client names (the review of a removal). */
  highlightRemoved?: boolean;
  className?: string;
}

/**
 * A citation as rendered (`.kb-cite`): the quote in the serif face, then
 * the reference. The vendor-confidential client form has no quote and is
 * shown as the server wrote it.
 */
export function VendorCitation({
  citation,
  highlightRemoved = false,
  className,
}: VendorCitationProps) {
  const { parts } = citation;
  return (
    <div
      className={cn(
        'mt-1.5 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700 [overflow-wrap:anywhere]',
        className,
      )}
    >
      {parts.excerpt !== undefined ? (
        <>
          <span className="font-serif text-[14px] text-ink-900">
            “<RedactedText text={parts.excerpt} highlight={highlightRemoved} />”
            {parts.answer_label ? ` (${parts.answer_label})` : ''}
          </span>{' '}
          <span>— {citationReference(citation)}</span>
        </>
      ) : (
        <span>{citation.text}</span>
      )}
    </div>
  );
}

/** The neutral "Vendor-confidential" chip (Decision 60): its quote never reaches client pages or exports. */
export function VendorConfidentialChip({
  className,
  children = 'Vendor-confidential',
}: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span
      title="Client pages and exports cite it by title and date only, without the quote."
      className={cn(
        'inline-flex h-5 flex-none items-center gap-1 whitespace-nowrap rounded-full border border-ink-100 bg-paper-100 px-2 text-[11px] font-medium text-ink-700',
        className,
      )}
    >
      <ShieldCheck className="h-[11px] w-[11px]" aria-hidden="true" />
      {children}
    </span>
  );
}

/** The mark of each kind of discovery source, as in `citeOf` of the discovery prototype. */
const DISCOVERY_SOURCE_ICON = {
  interview: MessageSquare,
  questionnaire: List,
  follow_up: Send,
  document: FileText,
  data: Database,
} as const;

export type SourceCiteKind = keyof typeof DISCOVERY_SOURCE_ICON;

/**
 * A citation to where a piece of discovery evidence came from (T-0188):
 * an interview line, a questionnaire or follow-up answer, a document page
 * or a data extract's rows. The `.cite` pill of the discovery prototype;
 * it opens nothing, so it is text, not a button. A withdrawn item's
 * citation stays in place, struck through (`.cite.broken`, Decision 54.20).
 */
export function SourceCite({
  kind,
  label,
  location,
  withdrawn = false,
  title,
  className,
}: {
  kind: SourceCiteKind;
  label: string;
  location: string | null;
  withdrawn?: boolean;
  /** Hover text, for example why it was withdrawn. */
  title?: string;
  className?: string;
}) {
  const Icon = DISCOVERY_SOURCE_ICON[kind];
  return (
    <span
      title={title ?? (location ? `${label} · ${location}` : label)}
      className={cn(
        'inline-flex h-6 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border pl-2 pr-2 text-[12px] font-medium',
        withdrawn
          ? 'border-cite-broken bg-cite-chip-bg text-cite-broken line-through decoration-1'
          : 'border-cite-chip-border bg-cite-chip-bg text-cite-chip-ink',
        className,
      )}
    >
      <Icon className="h-3 w-3 flex-none" aria-hidden="true" />
      <span className="truncate">{label}</span>
      {location ? (
        <span className="truncate font-mono text-[11px] font-normal text-ink-500">{location}</span>
      ) : null}
      {withdrawn ? <span className="sr-only"> (withdrawn)</span> : null}
    </span>
  );
}

/**
 * A citation to an interview line (T-0187): the interview code and the
 * time or paragraph, `S3 00:04:31` or `S6 para 4` (Decision 54.17). The
 * `.cite` pill with the message mark of the discovery prototype; it opens
 * nothing, so it is text, not a button.
 */
export function InterviewCite({
  code,
  at,
  className,
}: {
  code: string;
  at: string | null;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full border border-cite-chip-border bg-cite-chip-bg pl-2 pr-2 text-[12px] font-medium text-cite-chip-ink',
        className,
      )}
    >
      <MessageSquare className="h-3 w-3" aria-hidden="true" />
      {code}
      {at ? <span className="font-mono text-[11px] font-normal text-ink-500">{at}</span> : null}
    </span>
  );
}
